import React, { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControlLabel, Switch, FormControl, InputLabel, Select, MenuItem, RadioGroup, Radio, FormLabel, Box } from '@mui/material';

export interface UnassignVoucherPayload {
  reason: string;
  finePaid: boolean;
  fineParty: 'worker' | 'optima';
  note?: string;
  active: boolean; // true => active again, false => written off
}

export default function UnassignVoucherDialog({ open, onClose, onConfirm }: { open: boolean; onClose: () => void; onConfirm: (payload: UnassignVoucherPayload) => Promise<void> | void }) {
  const [reason, setReason] = useState('');
  const [finePaid, setFinePaid] = useState(false);
  const [fineParty, setFineParty] = useState<'worker' | 'optima'>('optima');
  const [note, setNote] = useState('');
  const [activeAgain, setActiveAgain] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = useMemo(() => reason.trim().length > 0 && (!finePaid || !!fineParty), [reason, finePaid, fineParty]);

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm({ reason: reason.trim(), finePaid, fineParty, note: note.trim() || undefined, active: activeAgain });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setReason('');
    setFinePaid(false);
    setFineParty('optima');
    setNote('');
    setActiveAgain(true);
  };

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Unassign Voucher</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} required fullWidth />
          <FormControlLabel control={<Switch checked={finePaid} onChange={(e) => setFinePaid(e.target.checked)} />} label="Fine paid?" />
          <FormControl fullWidth disabled={!finePaid}>
            <InputLabel id="fine-party-label">Fine paid by</InputLabel>
            <Select labelId="fine-party-label" label="Fine paid by" value={fineParty} onChange={(e) => setFineParty(e.target.value as any)}>
              <MenuItem value="optima">Optima</MenuItem>
              <MenuItem value="worker">Worker</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Explanation (optional)" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} fullWidth />
          <Box>
            <FormLabel id="voucher-status">Voucher after unassign</FormLabel>
            <RadioGroup row aria-labelledby="voucher-status" value={activeAgain ? 'active' : 'written_off'} onChange={(e) => setActiveAgain(e.target.value === 'active')}>
              <FormControlLabel value="active" control={<Radio />} label="Active again" />
              <FormControlLabel value="written_off" control={<Radio />} label="Written off" />
            </RadioGroup>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!canSubmit || submitting}>Unassign</Button>
      </DialogActions>
    </Dialog>
  );
}

